import { useParams, Navigate, Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import { Reveal, Label } from "@/components/motion";
import { destinationBySlug, useTitle } from "@/data/site";

export default function DestinationDetail() {
  const { slug } = useParams();
  const d = destinationBySlug(slug);
  useTitle(d ? d.seo : "Destinations | Urban Trail");

  if (!d) return <Navigate to="/destinations" replace />;

  return (
    <div data-testid={`destination-page-${d.slug}`}>
      <PageHero
        image={d.heroImage}
        label={`${d.region} Destination`}
        titleLines={d.heroTitle}
        sub={d.heroSub}
        tall
      />

      <section className="bg-white">
        <div className="mx-auto max-w-[1600px] px-6 py-28 lg:px-12 lg:py-36">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-3">
              <Reveal>
                <Label>{d.name}</Label>
              </Reveal>
            </div>
            <div className="lg:col-span-8 lg:col-start-5">
              <Reveal>
                <p className="max-w-3xl font-serif text-2xl font-light leading-relaxed tracking-tight text-ink sm:text-3xl lg:text-4xl">
                  {d.intro}
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {d.editorial.map((s, i) => (
        <section key={s.label} className={i % 2 === 0 ? "border-t border-mist bg-white" : "border-t border-mist bg-[#F7F7F6]"}>
          <div className="mx-auto max-w-[1600px] px-6 py-24 lg:px-12 lg:py-36">
            <div className="grid items-center gap-14 lg:grid-cols-12">
              <Reveal className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="group overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    className="aspect-[16/10] w-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.03]"
                  />
                </div>
              </Reveal>
              <div className={`lg:col-span-4 ${i % 2 === 1 ? "lg:order-1 lg:col-start-1" : "lg:col-start-9"}`}>
                <Reveal>
                  <Label>{s.label}</Label>
                </Reveal>
                <Reveal delay={0.08}>
                  <h2 className="mt-6 font-serif text-3xl font-light leading-tight tracking-tight text-ink sm:text-4xl">
                    {s.title}
                  </h2>
                </Reveal>
                <Reveal delay={0.16}>
                  <p className="mt-6 text-base leading-relaxed text-graphite">{s.text}</p>
                </Reveal>
              </div>
            </div>
          </div>
        </section>
      ))}

      <section className="bg-ink text-white">
        <div className="mx-auto max-w-[1600px] px-6 py-28 lg:px-12 lg:py-36">
          <Reveal>
            <Label>Experiences</Label>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 font-serif text-4xl font-light leading-[1.0] tracking-tight sm:text-5xl">
              Experience {d.name}
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-x-10 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
            {d.experiences.map((exp, i) => (
              <Reveal key={exp.title} delay={(i % 3) * 0.08}>
                <div data-testid={`experience-card-${i}`} className="group">
                  <div className="overflow-hidden">
                    <img
                      src={exp.image}
                      alt={exp.title}
                      loading="lazy"
                      className="aspect-[4/5] w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]"
                    />
                  </div>
                  <h3 className="mt-6 font-serif text-2xl font-light tracking-tight">{exp.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/60">{exp.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative h-[70vh] min-h-[480px] w-full overflow-hidden">
        <img src={d.feature.image} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 mx-auto flex h-full max-w-[1600px] items-center px-6 lg:px-12">
          <Reveal>
            <p
              data-testid="destination-feature-overlay"
              className="font-serif text-5xl font-light italic leading-none tracking-tight text-white sm:text-6xl lg:text-8xl"
            >
              {d.feature.overlay}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-[1600px] px-6 py-24 lg:px-12 lg:py-32">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-3">
              <Reveal>
                <Label>Travel Notes</Label>
              </Reveal>
            </div>
            <div className="lg:col-span-8 lg:col-start-5">
              {d.travelNotes && d.travelNotes.length > 0 ? (
                <Reveal>
                  <div className="grid max-w-4xl gap-x-12 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
                    {d.travelNotes.map((n) => (
                      <div key={n.title} className="border-t border-ink/10 pt-6">
                        <p className="label text-gold">{n.title}</p>
                        <p className="mt-4 text-[15px] leading-relaxed text-graphite">{n.text}</p>
                      </div>
                    ))}
                  </div>
                </Reveal>
              ) : (
                <Reveal>
                  <p className="max-w-2xl text-lg leading-relaxed text-graphite">
                    Every season paints {d.name} differently. Tell us when you'd like to
                    travel, and we'll advise you personally on the best routes, stays
                    and experiences for your dates.
                  </p>
                </Reveal>
              )}
              <Reveal delay={0.1}>
                <Link
                  to="/contact"
                  data-testid="destination-notes-cta"
                  className="link-arrow mt-8 text-ink hover:text-gold"
                >
                  Ask our travel advisors <span aria-hidden>→</span>
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <CtaBand
        image={d.heroImage}
        title={d.ctaTitle}
        sub="Share your dates, your pace and your idea of a perfect day — we'll take it from there."
        primaryText={d.ctaButton}
        primaryTo="/contact"
        secondaryText="All Destinations"
        secondaryTo="/destinations"
      />
    </div>
  );
}
